import { Box, DialogActions, DialogContent, Divider, Modal, ModalDialog, Typography } from '@mui/joy';
import React, { FC, FormEvent } from 'react';
import { TbSend } from 'react-icons/tb';
import { useStore } from 'zustand';

import type { PatientCreateRequest } from '@MP/shared/api/hooks/patient';
import { RectangleButton } from '@MP/shared/components/Button';
import ConfirmDialog from '@MP/shared/components/dialog/ConfirmDialog';
import { useMinddSnackbar } from '@MP/shared/components/MinddSnackbarProvider';
import { usePatientStore } from '@MP/shared/providers/PatientProvider';

import { PatientAddFormProvider } from '../../providers/PatientAddFormProvider';
import usePatientInvitationRepository from '../../repositories/usePatientInvitationRepository';
import {
  createPatientAddFormDataStore,
  PatientAddFormDataStoreApi,
  selectIsValid,
} from '../../stores/createPatientAddFormDataStore';
import { PatientInvitationViewModel } from '../../stores/PatientInvitationViewModel';
import PatientAddForm from '../PatientAddForm';

const PatientAddDialog: FC<{ open: boolean; orgId: number; onClose: () => void }> = ({ open, orgId, onClose }) => {
    const [formDataApi, setFormDataApi] = React.useState<PatientAddFormDataStoreApi>(() =>
      createPatientAddFormDataStore(orgId, true)
    );
    const repository = usePatientInvitationRepository();
    const [viewModelApi] = React.useState(() => PatientInvitationViewModel.createStore(repository));

    const isValid = useStore(formDataApi, selectIsValid);
    const isSubmitting = useStore(viewModelApi, (s) => s.isSubmitting);
    const openWarning = useStore(viewModelApi, (s) => s.openWarning);
    const submit = useStore(viewModelApi, (s) => s.submit);
    const confirmDuplicate = useStore(viewModelApi, (s) => s.confirmDuplicate);

    const addTempUser = usePatientStore((s) => s.addTempUser);
    const snackbar = useMinddSnackbar();

    React.useEffect(() => {
      if (!open) {
        setFormDataApi(createPatientAddFormDataStore(orgId, true));
        viewModelApi.getState().reset();
      }
    }, [open, orgId, viewModelApi]);

    const getFormFields = () => {
      const { code, name, phoneNumber, birth } = formDataApi.getState();
      return { orgId, code, name, phoneNumber, birth };
    };

    const notifySent = (newUser: PatientCreateRequest) => {
      addTempUser(newUser);
      snackbar.add('환자 추가 동의서 전송이 완료되었습니다.');
      onClose();
    };

    const handleSubmit = async (e: FormEvent) => {
      e.preventDefault();
      if (!isValid) {
        return;
      }
      const newUser = await submit(getFormFields());
      if (newUser) {
        notifySent(newUser);
      }
    };

    const handleDuplicatePhoneNumberResult = async (result: boolean) => {
      if (!isValid) {
        return;
      }
      const newUser = await confirmDuplicate({ fields: getFormFields(), result });
      if (newUser) {
        notifySent(newUser);
      }
    };

    return (
      <>
        <Modal open={open}>
          <ModalDialog sx={{ gap: '16px' }} component={'form'} onSubmit={handleSubmit}>
            <Box>
              <Typography level="h4" fontWeight={600}>
                환자 추가
              </Typography>
              <Typography level="body-sm" fontSize={15}>
                환자의 휴대전화번호로 발송되며, <b>환자의 동의가 완료되면</b> 환자 목록에 추가됩니다.
              </Typography>
            </Box>
            <DialogContent sx={{ width: '500px' }}>
              <PatientAddFormProvider store={formDataApi}>
                <PatientAddForm requiredPhoneNumber />
              </PatientAddFormProvider>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ pt: 0 }}>
              <RectangleButton type="submit" startDecorator={<TbSend />} loading={isSubmitting}>
                발송
              </RectangleButton>
              <RectangleButton color="neutral" variant="outlined" onClick={onClose}>
                취소
              </RectangleButton>
            </DialogActions>
          </ModalDialog>
        </Modal>
        <ConfirmDialog
          open={openWarning}
          warning
          okText="예"
          cancelText="아니오"
          title="동일한 전화번호를 가진 환자가 있습니다"
          message={
            <Box textAlign={'center'}>
              <Typography sx={{ fontSize: 14, color: '#333' }}>환자를 새로 등록하시겠습니까?</Typography>
            </Box>
          }
          onResult={handleDuplicatePhoneNumberResult}
        />
      </>
    );
};

export default PatientAddDialog;
